/**
 * AI to TipTap Content Converter
 * 
 * This utility solves the core issue where AI generates markdown but TipTap expects HTML.
 * It provides robust conversion functions that ensure content is properly formatted for TipTap editors.
 */

/**
 * Converts AI-generated markdown content to TipTap-compatible HTML
 * 
 * @param markdownContent - Raw markdown content from AI generation
 * @returns HTML string compatible with TipTap editor
 */
export function convertAIContentToTipTap(markdownContent: string): string {
  // Validate input
  if (!markdownContent || typeof markdownContent !== 'string') {
    return '';
  }

  try {
    // Clean and preprocess the markdown content
    const cleanedMarkdown = preprocessMarkdownForTipTap(markdownContent);
    
    // Convert markdown to HTML
    const htmlContent = convertMarkdownToHTML(cleanedMarkdown);
    
    // Post-process HTML for TipTap compatibility
    const tiptapHTML = postProcessHTMLForTipTap(htmlContent);
    
    return tiptapHTML;
  } catch (error) {
    console.error('[AI-to-TipTap Converter] Error converting content:', error);
    
    // Fallback: return basic HTML structure
    return convertMarkdownToBasicHTML(markdownContent);
  }
}

/**
 * Preprocesses markdown content to handle common AI generation patterns
 */
function preprocessMarkdownForTipTap(markdown: string): string {
  console.log('🧹 Preprocessing Markdown:', {
    input: markdown,
    hasCodeFences: markdown.includes('```'),
    hasHtmlFences: markdown.includes('```html')
  });

  let processed = markdown.trim();
  const originalProcessed = processed;
  
  // Remove markdown code fences if they wrap the entire content
  processed = processed.replace(/^```markdown\s*\n([\s\S]*?)\n```$/g, '$1');
  processed = processed.replace(/^```html\s*\n([\s\S]*?)\n```$/g, '$1');
  processed = processed.replace(/^```\s*\n([\s\S]*?)\n```$/g, '$1');
  
  // Handle inline code fences around HTML content (common AI pattern)
  processed = processed.replace(/^```html\s*(.*?)\s*```$/gm, '$1');
  processed = processed.replace(/^```\s*(.*?)\s*```$/gm, '$1');
  
  if (processed !== originalProcessed) {
    console.log('🔧 Code fences removed:', {
      before: originalProcessed.substring(0, 50) + '...',
      after: processed.substring(0, 50) + '...',
      removedFences: true
    });
  }
  
  // Clean up excessive whitespace but preserve paragraph structure
  processed = processed.replace(/\n{3,}/g, '\n\n');
  
  // Ensure proper line endings
  processed = processed.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  
  // Handle common AI patterns that might break markdown parsing
  processed = processed.replace(/^[\s]*[-*_]{3,}[\s]*$/gm, '---'); // Normalize horizontal rules
  
  console.log('✅ Preprocessing complete:', {
    output: processed,
    wasModified: processed !== markdown
  });
  
  return processed;
}

/**
 * Converts markdown to HTML using a comprehensive markdown parser
 */
function convertMarkdownToHTML(markdown: string): string {
  let html = markdown;
  
  // Convert headers (must be done first to avoid conflicts)
  html = html.replace(/^######\s+(.*$)/gim, '<h6>$1</h6>');
  html = html.replace(/^#####\s+(.*$)/gim, '<h5>$1</h5>');
  html = html.replace(/^####\s+(.*$)/gim, '<h4>$1</h4>');
  html = html.replace(/^###\s+(.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^##\s+(.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^#\s+(.*$)/gim, '<h1>$1</h1>');
  
  // Convert horizontal rules
  html = html.replace(/^---$/gm, '<hr>');
  
  // Convert code blocks (before inline code)
  html = html.replace(/```([^`]*?)```/gs, '<pre><code>$1</code></pre>');
  
  // Convert inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  
  // Convert bold and italic (bold first to avoid conflicts)
  html = html.replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>');
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Convert strikethrough
  html = html.replace(/~~(.*?)~~/g, '<del>$1</del>');
  
  // Convert links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  
  // Convert images
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="rounded-md max-w-full" />');
  
  // Convert bullet lists
  html = convertBulletLists(html);
  
  // Convert numbered lists
  html = convertNumberedLists(html);
  
  // Convert blockquotes
  html = html.replace(/^>\s+(.*$)/gim, '<blockquote>$1</blockquote>');
  
  // Convert tables (basic support)
  html = convertTables(html);
  
  // Convert paragraphs (must be last)
  html = convertParagraphs(html);
  
  return html;
}

/**
 * Converts bullet lists to HTML
 */
function convertBulletLists(html: string): string {
  const lines = html.split('\n');
  const result: string[] = [];
  let inList = false;
  let listLevel = 0;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const bulletMatch = line.match(/^(\s*)[*+-]\s+(.*)$/);
    
    if (bulletMatch) {
      const indent = bulletMatch[1].length;
      const content = bulletMatch[2];
      const currentLevel = Math.floor(indent / 2);
      
      if (!inList) {
        result.push('<ul>');
        inList = true;
        listLevel = currentLevel;
      } else if (currentLevel > listLevel) {
        result.push('<ul>');
        listLevel = currentLevel;
      } else if (currentLevel < listLevel) {
        for (let j = listLevel; j > currentLevel; j--) {
          result.push('</ul>');
        }
        listLevel = currentLevel;
      }
      
      result.push(`<li>${content}</li>`);
    } else {
      if (inList) {
        for (let j = 0; j <= listLevel; j++) {
          result.push('</ul>');
        }
        inList = false;
        listLevel = 0;
      }
      result.push(line);
    }
  }
  
  // Close any remaining lists
  if (inList) {
    for (let j = 0; j <= listLevel; j++) {
      result.push('</ul>');
    }
  }
  
  return result.join('\n');
}

/**
 * Converts numbered lists to HTML
 */
function convertNumberedLists(html: string): string {
  const lines = html.split('\n');
  const result: string[] = [];
  let inList = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const numberMatch = line.match(/^\s*\d+\.\s+(.*)$/);
    
    if (numberMatch) {
      const content = numberMatch[1];
      
      if (!inList) {
        result.push('<ol>');
        inList = true;
      }
      
      result.push(`<li>${content}</li>`);
    } else {
      if (inList) {
        result.push('</ol>');
        inList = false;
      }
      result.push(line);
    }
  }
  
  // Close any remaining list
  if (inList) {
    result.push('</ol>');
  }
  
  return result.join('\n');
}

/**
 * Converts tables to HTML (basic support)
 */
function convertTables(html: string): string {
  const lines = html.split('\n');
  const result: string[] = [];
  let inTable = false;
  let isHeaderRow = true;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Check if this is a table row
    if (line.includes('|') && line.split('|').length > 2) {
      const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell !== '');
      
      // Skip separator rows
      if (cells.every(cell => /^:?-+:?$/.test(cell))) {
        isHeaderRow = false;
        continue;
      }
      
      if (!inTable) {
        result.push('<table>');
        inTable = true;
        isHeaderRow = true;
      }
      
      const tag = isHeaderRow ? 'th' : 'td';
      const rowHtml = `<tr>${cells.map(cell => `<${tag}>${cell}</${tag}>`).join('')}</tr>`;
      result.push(rowHtml);
      
      if (isHeaderRow) {
        isHeaderRow = false;
      }
    } else {
      if (inTable) {
        result.push('</table>');
        inTable = false;
      }
      result.push(line);
    }
  }
  
  // Close any remaining table
  if (inTable) {
    result.push('</table>');
  }
  
  return result.join('\n');
}

/**
 * Converts line breaks to paragraphs
 */
function convertParagraphs(html: string): string {
  // Split by double line breaks to identify paragraphs
  const paragraphs = html.split(/\n\s*\n/).filter(p => p.trim());
  
  return paragraphs.map(paragraph => {
    const trimmed = paragraph.trim();
    
    // Don't wrap if already wrapped in a block element
    if (trimmed.match(/^<(h[1-6]|ul|ol|blockquote|table|pre|hr|div)/i)) {
      return trimmed;
    }
    
    // Don't wrap if it's just a closing tag
    if (trimmed.match(/^<\//)) {
      return trimmed;
    }
    
    // Wrap in paragraph if it has content
    if (trimmed) {
      return `<p>${trimmed.replace(/\n/g, '<br>')}</p>`;
    }
    
    return trimmed;
  }).join('\n\n');
}

/**
 * Post-processes HTML to ensure TipTap compatibility
 */
function postProcessHTMLForTipTap(html: string): string {
  let processed = html;
  
  // Clean up extra whitespace
  processed = processed.trim();
  
  // Remove empty paragraphs
  processed = processed.replace(/<p>\s*<\/p>/g, '');
  
  // Ensure proper paragraph structure
  if (processed && !processed.match(/^<(p|h[1-6]|ul|ol|blockquote|div|table|pre|hr)/i)) {
    processed = `<p>${processed}</p>`;
  }
  
  // Clean up multiple consecutive line breaks
  processed = processed.replace(/\n{3,}/g, '\n\n');
  
  return processed;
}

/**
 * Fallback conversion for when the main converter fails
 */
function convertMarkdownToBasicHTML(markdown: string): string {
  let html = markdown;
  
  // Convert headers
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');
  
  // Convert bold and italic
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Convert inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  
  // Convert links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  
  // Convert line breaks to paragraphs
  const paragraphs = html.split(/\n\s*\n/).filter(p => p.trim());
  html = paragraphs.map(p => `<p>${p.replace(/\n/g, ' ')}</p>`).join('');
  
  // If no paragraphs were created, wrap in a single paragraph
  if (!html.includes('<p>')) {
    html = `<p>${html}</p>`;
  }
  
  return html;
}

/**
 * Detects if content is already in TipTap HTML format
 */
export function isTipTapHTML(content: string): boolean {
  if (!content || typeof content !== 'string') {
    return false;
  }
  
  // Check for TipTap-specific HTML patterns
  const tiptapPatterns = [
    /<p[^>]*>.*<\/p>/s,
    /<h[1-6][^>]*>.*<\/h[1-6]>/s,
    /<ul[^>]*>.*<\/ul>/s,
    /<ol[^>]*>.*<\/ol>/s,
    /<strong[^>]*>.*<\/strong>/s,
    /<em[^>]*>.*<\/em>/s
  ];
  
  return tiptapPatterns.some(pattern => pattern.test(content));
}

/**
 * Detects if content appears to be markdown
 */
export function isMarkdown(content: string): boolean {
  if (!content || typeof content !== 'string') {
    return false;
  }
  
  // Check for common markdown patterns
  const markdownPatterns = [
    /^#{1,6}\s+/m, // Headers
    /\*\*.*?\*\*/, // Bold
    /\*.*?\*/, // Italic
    /\[.*?\]\(.*?\)/, // Links
    /^[\*\-\+]\s+/m, // Bullet lists
    /^\d+\.\s+/m, // Numbered lists
    /`.*?`/, // Inline code
    /^```/m, // Code blocks
    /^>\s+/m, // Blockquotes
    /\|.*\|/, // Tables
  ];
  
  return markdownPatterns.some(pattern => pattern.test(content));
}

/**
 * Smart converter that detects content type and converts if needed
 */
export function ensureTipTapFormat(content: string): string {
  console.log('🔄 ensureTipTapFormat Input:', {
    content: content,
    length: content.length,
    type: typeof content
  });

  if (!content || typeof content !== 'string') {
    console.log('❌ Invalid content, returning empty string');
    return '';
  }

  // If already TipTap HTML, return as-is
  if (isTipTapHTML(content)) {
    console.log('✅ Content is already TipTap HTML, returning as-is');
    return content;
  }
  
  // If it looks like markdown, convert it
  if (isMarkdown(content)) {
    console.log('📝 Content detected as Markdown, converting...');
    const result = convertAIContentToTipTap(content);
    console.log('✅ Markdown conversion complete:', {
      original: content.substring(0, 50) + '...',
      converted: result.substring(0, 50) + '...'
    });
    return result;
  }
  
  // If it's plain text, wrap in paragraph
  if (content.trim() && !content.includes('<')) {
    console.log('📄 Content is plain text, wrapping in paragraph');
    const result = `<p>${content.replace(/\n/g, '<br>')}</p>`;
    console.log('✅ Plain text wrapped:', result);
    return result;
  }
  
  // Otherwise, assume it's HTML and post-process for TipTap
  console.log('🔧 Content assumed to be HTML, post-processing...');
  const result = postProcessHTMLForTipTap(content);
  console.log('✅ HTML post-processing complete:', {
    original: content.substring(0, 50) + '...',
    processed: result.substring(0, 50) + '...'
  });
  return result;
}

/**
 * Utility for debugging content conversion
 */
export function debugContentConversion(content: string): {
  originalType: 'markdown' | 'html' | 'plain' | 'empty';
  originalLength: number;
  convertedLength: number;
  preview: string;
  wasConverted: boolean;
} {
  if (!content) {
    return {
      originalType: 'empty',
      originalLength: 0,
      convertedLength: 0,
      preview: '',
      wasConverted: false
    };
  }
  
  const originalType = isTipTapHTML(content) ? 'html' : 
                      isMarkdown(content) ? 'markdown' : 'plain';
  
  const converted = ensureTipTapFormat(content);
  const wasConverted = content !== converted;
  
  return {
    originalType,
    originalLength: content.length,
    convertedLength: converted.length,
    preview: converted.substring(0, 100) + (converted.length > 100 ? '...' : ''),
    wasConverted
  };
}

/**
 * Batch converter for multiple content items
 */
export function convertMultipleAIContent(contents: string[]): string[] {
  return contents.map(content => ensureTipTapFormat(content));
}

/**
 * Converts AI content and provides metadata about the conversion
 */
export function convertAIContentWithMetadata(content: string): {
  html: string;
  metadata: {
    originalFormat: 'markdown' | 'html' | 'plain' | 'empty';
    wasConverted: boolean;
    originalLength: number;
    convertedLength: number;
  };
} {
  const debug = debugContentConversion(content);
  
  return {
    html: ensureTipTapFormat(content),
    metadata: {
      originalFormat: debug.originalType,
      wasConverted: debug.wasConverted,
      originalLength: debug.originalLength,
      convertedLength: debug.convertedLength
    }
  };
} 